import { graphql } from 'gatsby'
import React, { FC, useEffect, useState } from 'react'
import { Col, Row } from 'styled-bootstrap-grid'

import { Card, GRID_UNIT, mediaQuerySizes } from 'mf-ui-components'

import rawlang, * as l10n from '../../../../utils/l10n'
import { getLocaleFromCountry } from '../../../../utils/locale'
import { formatRoundedCurrency } from '../../../../utils/currency'
import { useIsViewportBelow } from '../../../../utils/useIsViewportBelow'
import { ExpectedPerformanceValues } from './ExpectedPerformanceValues'
import {
  DEFAULT_COMPLEXITY_LEVEL,
  DEFAULT_RISK_LEVEL,
  computePortfolioProjection,
  getChartDataSet,
  getFormErrors,
} from './libs'
import {
  Container,
  MobileChartContainer,
  RegularScreenForm,
  SmallScreenForm,
} from './ExpectedPerformance.styles'
import { ChartData } from './types'
import { useModelPortfolios } from './useModelPortfolios'
import { Chart } from './Chart'
import { Form } from './Form'
import {
  ModelPortfolio,
  type ComplexityLevel,
  type RiskLevel,
} from '../../../../types/ModelPortfolio'
import { findModelPortfolio } from '../../../../utils/modelPortfolios'

const lang = rawlang as 'en' | 'it'

type Props = {
  marginBottom?: number
  hiddenAt?: string
}

const ExpectedPerformance: FC<Props> = ({ marginBottom, hiddenAt }) => {
  const [initialContribution, setInitialContribution] = useState<number>(15_000)
  const [monthlyContribution, setMonthlyContribution] = useState<number>(250)
  const [timeHorizon, setTimeHorizon] = useState<number>(20)
  const [portfolioRiskLevel, setPortfolioRiskLevel] =
    useState<RiskLevel>(DEFAULT_RISK_LEVEL)
  const [sliderRiskLevel, setSliderRiskLevel] =
    useState<RiskLevel>(DEFAULT_RISK_LEVEL)
  const [complexityLevel, setComplexityLevel] = useState<ComplexityLevel>(
    DEFAULT_COMPLEXITY_LEVEL
  )
  const [currentModelPortfolio, setCurrentModelPortfolio] =
    useState<ModelPortfolio>()
  const [chartData, setChartData] = useState<ChartData>()
  const [errors, setErrors] = useState<{
    initialContribution?: string
    monthlyContribution?: string
  }>({
    initialContribution: undefined,
    monthlyContribution: undefined,
  })

  const isBelowTablet = useIsViewportBelow(mediaQuerySizes.tablet)

  const { modelPortfolios, isLoaded } = useModelPortfolios()

  useEffect(() => {
    if (!modelPortfolios) return

    const defaultModelPortfolio = modelPortfolios.find(
      findModelPortfolio(
        process.env.GATSBY_COUNTRY!,
        portfolioRiskLevel,
        complexityLevel,
        'classic'
      )
    )
    setCurrentModelPortfolio(defaultModelPortfolio)
  }, [modelPortfolios])

  useEffect(() => {
    if (!currentModelPortfolio) return

    const { totalContribution, expectedValue } = getChartDataSet(
      computePortfolioProjection(
        currentModelPortfolio.expectedPerformance,
        timeHorizon,
        initialContribution,
        monthlyContribution
      ),
      initialContribution,
      monthlyContribution
    )

    setChartData({ totalContribution, expectedValue })
  }, [currentModelPortfolio])

  useEffect(() => {
    submitForm()
  }, [portfolioRiskLevel, timeHorizon])

  useEffect(() => {
    if (process.env.GATSBY_COUNTRY === 'IT')
      setComplexityLevel(initialContribution >= 50_000 ? 2 : 1)
  }, [initialContribution])

  const submitForm = () => {
    validateForm()

    if (Object.keys(errors).length === 0) {
      const modelPortfolio = modelPortfolios.find(
        findModelPortfolio(
          process.env.GATSBY_COUNTRY!,
          portfolioRiskLevel,
          complexityLevel,
          'classic'
        )
      )

      if (modelPortfolio) setCurrentModelPortfolio({ ...modelPortfolio })
    }
  }

  const validateForm = () => {
    const errors = getFormErrors(initialContribution, monthlyContribution)

    setErrors(errors)
  }

  const yFormat = (value: number) =>
    formatRoundedCurrency(
      getLocaleFromCountry(process.env.GATSBY_COUNTRY),
      process.env.GATSBY_CURRENCY,
      value
    )

  const form = (
    <Form
      errors={errors}
      isBelowTablet={isBelowTablet}
      submitForm={submitForm}
      validateForm={validateForm}
      initialContribution={initialContribution}
      setInitialContribution={setInitialContribution}
      monthlyContribution={monthlyContribution}
      setMonthlyContribution={setMonthlyContribution}
      setTimeHorizon={setTimeHorizon}
      timeHorizon={timeHorizon}
      sliderRiskLevel={sliderRiskLevel}
      setSliderRiskLevel={setSliderRiskLevel}
      setPortfolioRiskLevel={setPortfolioRiskLevel}
    />
  )

  const chart = chartData ? (
    <Chart chartData={chartData} yFormat={yFormat} />
  ) : null

  const performanceValues = chartData
    ? {
        contributionValue: chartData.totalContribution.data.slice(-1)[0].y,
        projectionValue: chartData.expectedValue.data.slice(-1)[0].y,
      }
    : null

  if (!isLoaded) return <p>Loading...</p>

  return (
    <Container marginBottom={marginBottom} hiddenAt={hiddenAt}>
      <Row>
        <Col col={12} lg={4}>
          <RegularScreenForm>
            <Card padding={GRID_UNIT * 4}>{form}</Card>
          </RegularScreenForm>
          <SmallScreenForm>{form}</SmallScreenForm>
        </Col>
        <Col col={12} lg={GRID_UNIT}>
          {isBelowTablet ? (
            <MobileChartContainer>
              {performanceValues && (
                <ExpectedPerformanceValues
                  contributionText={
                    l10n.expected_performance.contribution[lang]
                  }
                  contributionValue={yFormat(
                    performanceValues.contributionValue
                  )}
                  projectionText={
                    l10n.expected_performance.projected_value[lang]
                  }
                  projectionValue={yFormat(performanceValues.projectionValue)}
                  profitText={l10n.expected_performance.average_profit[lang]}
                  profitValue={yFormat(
                    performanceValues.projectionValue -
                      performanceValues.contributionValue
                  )}
                />
              )}

              {chart}
            </MobileChartContainer>
          ) : (
            chart
          )}
        </Col>
      </Row>
    </Container>
  )
}

ExpectedPerformance.displayName = 'ExpectedPerformance'

export default ExpectedPerformance

export const query = graphql`
  fragment expectedPerformanceFields on WPGraphQL_MfblocksExpectedPerformanceBlock {
    name
    attributes {
      id
      marginBottom
      hiddenAt
    }
  }
`
